import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/components/shared";
import { Mail, Phone, Globe, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const appStatus = {
  new: { label: "Yeni", cls: "bg-slate-900 text-white" },
  approved: { label: "Onaylandı", cls: "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20" },
  rejected: { label: "Reddedildi", cls: "bg-red-500/10 text-red-500 border border-red-500/20" },
};

export default function SupplierAppsPage() {
  const [apps, setApps] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const removeApp = async (id) => {
    if (confirmDel !== id) {
      setConfirmDel(id);
      return;
    }
    setBusy(id);
    try {
      await api.delete(`/supplier-applications/${id}`);
      setApps((as) => as.filter((a) => a.id !== id));
      toast.success("Başvuru kaydı silindi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
      setConfirmDel(null);
    }
  };

  useEffect(() => {
    api.get("/supplier-applications").then((r) => setApps(r.data)).catch(() => setApps([]));
  }, []);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/supplier-applications/${id}/${action}`);
      setApps((as) => as.map((a) => (a.id === id ? { ...a, status: action === "approve" ? "approved" : "rejected" } : a)));
      toast.success(action === "approve" ? "Başvuru onaylandı — tedarikçi hesabı oluşturuldu, şifre kurulum e-postası gönderildi" : "Başvuru reddedildi");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!apps) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;
  const newCount = apps.filter((a) => a.status === "new").length;

  return (
    <div className="space-y-8 fade-in max-w-4xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Web Sitesi</div>
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="supplier-apps-title">Tedarikçi Başvuruları</h1>
          {newCount > 0 && <Badge className="rounded-none bg-slate-900 text-white text-xs" data-testid="supplier-apps-new-count">{newCount} yeni</Badge>}
        </div>
        <p className="text-sm text-slate-500 mt-2">Landing sayfasındaki "Tedarikçi Olun" formundan gelen başvurular. Onaylanınca tedarikçi hesabı otomatik oluşturulur.</p>
      </div>

      {apps.length === 0 ? (
        <p className="text-slate-500 text-sm border border-slate-200 bg-white p-6" data-testid="no-supplier-apps">Henüz başvuru yok.</p>
      ) : (
        <div className="space-y-4">
          {apps.map((a) => {
            const st = appStatus[a.status] || appStatus.new;
            return (
              <div key={a.id} className="border border-slate-200 bg-white p-5" data-testid={`supplier-app-${a.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-lg tracking-tight">{a.company_name}</span>
                      <Badge className={`rounded-none text-[10px] ${st.cls}`} data-testid={`app-status-${a.id}`}>{st.label}</Badge>
                      {a.category && <Badge variant="outline" className="rounded-none text-xs border-slate-300 text-slate-600">{a.category}</Badge>}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">{a.contact_name} · {formatDate(a.created_at)}</div>
                    <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                      <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"><Mail size={13} /> {a.email}</a>
                      {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"><Phone size={13} /> {a.phone}</a>}
                      {a.website && (
                        <a href={a.website.startsWith("http") ? a.website : `https://${a.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-600 hover:underline">
                          <Globe size={13} /> {a.website}
                        </a>
                      )}
                    </div>
                  </div>
                  {a.status === "new" ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button onClick={() => act(a.id, "approve")} disabled={busy === a.id} data-testid={`approve-app-${a.id}`} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white gap-2 font-semibold transition-colors duration-200">
                        <CheckCircle2 size={14} /> Onayla
                      </Button>
                      <Button variant="outline" onClick={() => act(a.id, "reject")} disabled={busy === a.id} data-testid={`reject-app-${a.id}`} className="rounded-none border-red-500/30 text-red-500 hover:bg-red-500/10 gap-2 transition-colors duration-200">
                        <XCircle size={14} /> Reddet
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => removeApp(a.id)} disabled={busy === a.id} data-testid={`delete-app-${a.id}`} className={`rounded-none gap-1.5 h-9 shrink-0 transition-colors duration-200 ${confirmDel === a.id ? "bg-red-600 text-white border-red-600 hover:bg-red-700 hover:text-white" : "border-slate-200 text-slate-500 hover:bg-slate-100"}`}>
                      <Trash2 size={13} /> {confirmDel === a.id ? "Emin misiniz? Sil" : "Sil"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
